import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { Upload, Sparkles, TrendingUp, Clock, CheckCircle, Crown } from 'lucide-react';
import {
  trackLandingView,
  trackUploadStart,
  trackUploadComplete,
  trackStyleSelected,
  trackAIGenerationStarted,
  trackAIGenerationCompleted,
  trackPriceResultViewed,
  trackPublishClicked,
  trackBoostClicked,
  trackSuggestionChecked,
  trackSuggestionUnchecked
} from '../utils/aiAnalytics';
import {
  transformPropertyImage,
  saveAIProperty,
  getUserQuota,
  canUseAITransform,
  calculatePriceImpact as calcPriceImpact,
  getAISuggestions
} from '../services/aiTransformService';
import AIPaymentModal from '../components/AIPaymentModal';
import { auth } from '../firebase-config';

export default function AIPropertyTransform() {
  const navigate = useNavigate();
  const [step, setStep] = useState('upload'); // upload, transform, result
  const [propertyData, setPropertyData] = useState({
    address: '',
    area_m2: '',
    rooms: '',
    price: '',
    images: []
  });
  const [selectedStyle, setSelectedStyle] = useState('Modern');
  const [isProcessing, setIsProcessing] = useState(false);
  const [aiStartTime, setAiStartTime] = useState(null);
  const [userQuota, setUserQuota] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [transformedImages, setTransformedImages] = useState({});
  const [currentPropertyId, setCurrentPropertyId] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [checkedSuggestions, setCheckedSuggestions] = useState([]);

  // Track landing view on component mount
  useEffect(() => {
    trackLandingView();
    loadUserQuota();
  }, []);

  // Load user quota
  const loadUserQuota = async () => {
    const user = auth.currentUser;
    if (user) {
      const quota = await getUserQuota(user.uid);
      setUserQuota(quota);
    } else {
      // Anonymous user - free tier
      setUserQuota({
        tier: 'free',
        imagesRemaining: 1,
        stylesAvailable: ['modern'],
        maxImages: 1
      });
    }
  };

  const styles = [
    { id: 'modern', name: 'Modern', emoji: '🏢', description: 'Tiszta vonalak, semleges színek' },
    { id: 'luxury', name: 'Luxus', emoji: '💎', description: 'Prémium anyagok, elegáns' },
    { id: 'minimal', name: 'Minimal', emoji: '⚪', description: 'Egyszerű, letisztult' },
    { id: 'airbnb', name: 'Airbnb', emoji: '🏠', description: 'Meleg, vendégszerető' },
    { id: 'classic', name: 'Klasszikus', emoji: '🏛️', description: 'Időtlen, elegáns' }
  ];

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    
    // Track upload start on first image
    if (propertyData.images.length === 0 && files.length > 0) {
      trackUploadStart();
    }
    
    setPropertyData(prev => ({
      ...prev,
      images: [...prev.images, ...files].slice(0, 10)
    }));
  };

  const handleStartAnalysis = async () => {
    if (propertyData.images.length === 0) {
      alert('Kérlek tölts fel legalább 1 képet!');
      return;
    }

    // Check quota
    const user = auth.currentUser;
    if (user) {
      const canUse = await canUseAITransform(user.uid, propertyData.images.length);
      if (!canUse) {
        alert('Elérted az ingyenes kvótád! Válts PRO vagy PREMIUM csomagra.');
        setPaymentModalOpen(true);
        return;
      }
    }
    
    // Track upload complete
    trackUploadComplete(propertyData.images.length, propertyData);
    
    // Track AI generation started
    trackAIGenerationStarted(selectedStyle);
    
    setIsProcessing(true);
    const startTime = Date.now();
    setAiStartTime(startTime);
    
    try {
      // Transform first image with AI
      const result = await transformPropertyImage(
        propertyData.images[0],
        selectedStyle,
        'living room'
      );
      
      const duration = Date.now() - startTime;
      
      // Store transformed image
      setTransformedImages({
        [selectedStyle]: result.transformedImageUrl
      });
      
      // Track AI generation completed
      trackAIGenerationCompleted(selectedStyle, duration);
      
      // Generate AI suggestions
      const priceData = calculatePriceImpact();
      const suggestions = getAISuggestions(propertyData, priceData);
      setAiSuggestions(suggestions);
      
      // Save to Firebase
      if (user) {
        const propertyId = await saveAIProperty(user.uid, propertyData, {
          selectedStyle,
          originalImages: propertyData.images.map(img => URL.createObjectURL(img)),
          transformedImages: [result.transformedImageUrl],
          priceImpact: priceData,
          tier: userQuota?.tier || 'free'
        });
        setCurrentPropertyId(propertyId);
      }
      
      setIsProcessing(false);
      setStep('transform');
      
      // Track price result viewed after a short delay
      setTimeout(() => {
        trackPriceResultViewed(priceData);
      }, 500);
      
    } catch (error) {
      console.error('AI transformation error:', error);
      setIsProcessing(false);
      alert('Hiba történt az AI feldolgozás során. Próbáld újra!');
    }
  };

  const handleStyleChange = async (style) => {
    // Check if style is available in user's tier
    if (userQuota && !userQuota.stylesAvailable.includes(style.toLowerCase())) {
      alert(`${style} stílus csak PRO/PREMIUM csomagban érhető el!`);
      setPaymentModalOpen(true);
      return;
    }

    // Track style selection
    trackStyleSelected(style);
    trackAIGenerationStarted(style);
    
    setSelectedStyle(style);
    setIsProcessing(true);
    const startTime = Date.now();
    
    try {
      // Check if already transformed with this style
      if (transformedImages[style]) {
        setIsProcessing(false);
        return;
      }

      // Transform with new style
      const result = await transformPropertyImage(
        propertyData.images[0],
        style,
        'living room'
      );
      
      const duration = Date.now() - startTime;
      
      // Store transformed image
      setTransformedImages(prev => ({
        ...prev,
        [style]: result.transformedImageUrl
      }));
      
      trackAIGenerationCompleted(style, duration);
      setIsProcessing(false);
      
    } catch (error) {
      console.error('Style change error:', error);
      setIsProcessing(false);
      
      // Fallback: simulate transformation
      setTimeout(() => {
        const duration = Date.now() - startTime;
        trackAIGenerationCompleted(style, duration);
        setIsProcessing(false);
      }, 1500);
    }
  };

  const calculatePriceImpact = () => {
    return calcPriceImpact(propertyData, selectedStyle);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
      maximumFractionDigits: 0
    }).format(price);
  };

  const priceData = calculatePriceImpact();

  return (
    <>
      <Helmet>
        <title>AI Ingatlan Átalakító – Eladási Optimalizálás | HoppHomes</title>
        <meta name="description" content="1 kattintás → jobb lakás verzió. AI-alapú ingatlan átalakítás és eladási optimalizálás. Nézd meg, mennyivel többet ér a lakásod!" />
      </Helmet>

      {/* Payment Modal */}
      <AIPaymentModal 
        open={paymentModalOpen} 
        onClose={() => setPaymentModalOpen(false)}
        currentTier={userQuota?.tier || 'free'}
      />

      <div className="min-h-screen bg-[#0a0a0a]">
        {/* Page Header */}
        <div className="border-b border-white/[0.06] px-6 py-5">
          <h1 className="text-2xl font-black text-white">AI Kereső</h1>
          <p className="text-[#9ca3af] text-sm mt-0.5">Ingatlan optimalizálás mesterséges intelligenciával</p>
        </div>

        {/* UPLOAD STEP */}
        {step === 'upload' && (
          <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
            {/* Hero */}
            <div className="text-center mb-8 sm:mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 border border-green-500/40 rounded-full text-white text-xs sm:text-sm font-medium mb-4 sm:mb-6">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <Sparkles className="w-4 h-4" />
                AI-alapú ingatlan optimalizálás
              </div>
              
              <h1 className="text-3xl sm:text-4xl md:text-6xl font-extrabold text-white mb-3 sm:mb-4">
                Mennyivel többet ér a lakásod?
              </h1>
              
              <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-6 sm:mb-8">
                Töltsd fel a képeket, és <span className="font-bold text-yellow-400">AI-val megmutatjuk</span> a jobb verziót + az árhatást
              </p>

              {/* User Quota Display */}
              {userQuota && (
                <div className="mb-6 sm:mb-8">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/[0.08] rounded-full">
                    <Crown className="w-4 h-4 text-yellow-400" />
                    <span className="text-sm text-white">
                      {userQuota.tier === 'free' && `Ingyenes: ${userQuota.imagesRemaining}/${userQuota.maxImages} kép`}
                      {userQuota.tier === 'pro' && `PRO: ${userQuota.imagesRemaining}/${userQuota.maxImages} kép`}
                      {userQuota.tier === 'premium' && `PREMIUM: ${userQuota.imagesRemaining}/${userQuota.maxImages} kép`}
                    </span>
                    {userQuota.tier === 'free' && (
                      <button
                        onClick={() => setPaymentModalOpen(true)}
                        className="ml-2 px-3 py-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold rounded-full hover:from-purple-700 hover:to-pink-700 transition-all"
                      >
                        Upgrade
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3 sm:gap-6 max-w-2xl mx-auto mb-8 sm:mb-12">
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-white/10">
                  <div className="text-2xl sm:text-3xl font-bold text-secondary mb-1">+9.7%</div>
                  <div className="text-xs sm:text-sm text-gray-400">Átlag árhatás</div>
                </div>
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-white/10">
                  <div className="text-2xl sm:text-3xl font-bold text-secondary mb-1">14-30</div>
                  <div className="text-xs sm:text-sm text-gray-400">Nap eladás</div>
                </div>
                <div className="bg-white/5 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-white/10">
                  <div className="text-2xl sm:text-3xl font-bold text-secondary mb-1">3-5s</div>
                  <div className="text-xs sm:text-sm text-gray-400">AI elemzés</div>
                </div>
              </div>
            </div>

            {/* Upload Form */}
            <div className="bg-[#111111] border border-white/[0.07] rounded-2xl p-4 sm:p-6 md:p-8 mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">Ingatlan adatok</h2>
              
              <div className="space-y-4 sm:space-y-6">
                {/* Address */}
                <div>
                  <label className="block text-white font-medium mb-2 text-sm sm:text-base">Cím</label>
                  <input
                    type="text"
                    value={propertyData.address}
                    onChange={(e) => setPropertyData(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="pl. Budapest, XIII. kerület"
                    className="w-full px-4 py-3 bg-white/5 border border-white/[0.08] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-secondary text-sm sm:text-base"
                  />
                </div>

                {/* Grid inputs */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-white font-medium mb-2 text-sm sm:text-base">m²</label>
                    <input
                      type="number"
                      value={propertyData.area_m2}
                      onChange={(e) => setPropertyData(prev => ({ ...prev, area_m2: e.target.value }))}
                      placeholder="65"
                      className="w-full px-4 py-3 bg-white/5 border border-white/[0.08] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-secondary text-sm sm:text-base"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-white font-medium mb-2 text-sm sm:text-base">Szobák</label>
                    <input
                      type="number"
                      value={propertyData.rooms}
                      onChange={(e) => setPropertyData(prev => ({ ...prev, rooms: e.target.value }))}
                      placeholder="2"
                      className="w-full px-4 py-3 bg-white/5 border border-white/[0.08] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-secondary text-sm sm:text-base"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-white font-medium mb-2 text-sm sm:text-base">Irányár (Ft)</label>
                    <input
                      type="number"
                      value={propertyData.price}
                      onChange={(e) => setPropertyData(prev => ({ ...prev, price: e.target.value }))}
                      placeholder="62000000"
                      className="w-full px-4 py-3 bg-white/5 border border-white/[0.08] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-secondary text-sm sm:text-base"
                    />
                  </div>
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-white font-medium mb-2 text-sm sm:text-base">
                    Képek feltöltése (max 10)
                  </label>
                  
                  <div className="border-2 border-dashed border-white/30 rounded-xl p-6 sm:p-8 text-center hover:border-secondary transition-colors cursor-pointer">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="image-upload"
                    />
                    <label htmlFor="image-upload" className="cursor-pointer">
                      <Upload className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                      <p className="text-white font-medium mb-2 text-sm sm:text-base">Kattints vagy húzd ide a képeket</p>
                      <p className="text-xs sm:text-sm text-gray-400">JPG, PNG (max 10 kép)</p>
                    </label>
                  </div>

                  {/* Uploaded images preview */}
                  {propertyData.images.length > 0 && (
                    <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-3">
                      {propertyData.images.map((img, idx) => (
                        <div key={idx} className="relative aspect-square bg-white/5 rounded-lg overflow-hidden">
                          <img 
                            src={URL.createObjectURL(img)} 
                            alt={`Upload ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                          <button
                            onClick={() => setPropertyData(prev => ({
                              ...prev,
                              images: prev.images.filter((_, i) => i !== idx)
                            }))}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center text-xs hover:bg-red-600"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={handleStartAnalysis}
              disabled={propertyData.images.length === 0}
              className="w-full bg-secondary hover:bg-hover text-white font-bold px-6 py-4 sm:py-5 rounded-xl text-base sm:text-lg shadow-lg transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation flex items-center justify-center gap-2"
            >
              <Sparkles className="w-5 h-5" />
              🔴 Elemzés indítása
            </button>

            {/* Trust badges */}
            <div className="mt-6 sm:mt-8 text-center">
              <p className="text-xs sm:text-sm text-gray-400 mb-3">
                ✅ Ingyenes elemzés • ✅ 3-5 sec eredmény • ✅ Nincs regisztráció
              </p>
            </div>
          </div>
        )}

        {/* PROCESSING */}
        {isProcessing && step === 'upload' && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 border-4 border-secondary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-white text-lg sm:text-xl font-bold mb-2">AI dolgozik...</p>
              <p className="text-gray-400 text-sm sm:text-base">Elemezzük a képeket és készítjük az optimalizált verziót</p>
            </div>
          </div>
        )}

        {/* TRANSFORM STEP */}
        {step === 'transform' && (
          <div className="max-w-7xl mx-auto px-4 py-8 sm:py-12">
            {/* Header */}
            <div className="flex items-center justify-between mb-6 sm:mb-8">
              <button
                onClick={() => setStep('upload')}
                className="text-white hover:text-secondary transition-colors text-sm sm:text-base"
              >
                ← Vissza
              </button>
              <h1 className="text-xl sm:text-2xl font-bold text-white">AI Design</h1>
              <div className="w-16"></div>
            </div>

            {/* Before/After Comparison */}
            <div className="bg-[#161616] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-4 sm:p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4">
                {/* Before */}
                <div>
                  <p className="text-white font-medium mb-2 text-sm sm:text-base">Eredeti</p>
                  <div className="aspect-video bg-gradient-to-br from-gray-700 to-gray-900 rounded-xl overflow-hidden">
                    {propertyData.images[0] && (
                      <img 
                        src={URL.createObjectURL(propertyData.images[0])} 
                        alt="Original"
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                </div>

                {/* After */}
                <div className="relative">
                  <p className="text-white font-medium mb-2 text-sm sm:text-base">
                    AI Átalakított ({selectedStyle})
                  </p>
                  <div className="aspect-video bg-gradient-to-br from-blue-900 to-purple-900 rounded-xl overflow-hidden relative">
                    {propertyData.images[0] && (
                      <img 
                        src={URL.createObjectURL(propertyData.images[0])} 
                        alt="Transformed"
                        className="w-full h-full object-cover opacity-90 brightness-110"
                        style={{ filter: 'saturate(1.2) contrast(1.1)' }}
                      />
                    )}
                    {isProcessing && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                    <div className="absolute top-2 right-2 bg-secondary px-3 py-1 rounded-full text-white text-xs font-bold">
                      AI ✨
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Style Selector */}
            <div className="mb-6 sm:mb-8">
              <h3 className="text-white font-bold mb-3 sm:mb-4 text-base sm:text-lg">Válassz stílust</h3>
              <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-2 mobile-scroll-x">
                {styles.map((style) => (
                  <button
                    key={style.id}
                    onClick={() => handleStyleChange(style.name)}
                    className={`flex-shrink-0 px-4 sm:px-6 py-3 sm:py-4 rounded-xl font-medium transition-all touch-manipulation min-w-[120px] sm:min-w-[140px] ${
                      selectedStyle === style.name
                        ? 'bg-secondary text-white shadow-lg scale-105'
                        : 'bg-white/10 text-gray-300 hover:bg-white/20'
                    }`}
                  >
                    <div className="text-xl sm:text-2xl mb-1">{style.emoji}</div>
                    <div className="text-xs sm:text-sm font-bold">{style.name}</div>
                    <div className="text-[10px] sm:text-xs opacity-80">{style.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Price Impact */}
            <div className="bg-gradient-to-br from-green-900/30 to-blue-900/30 backdrop-blur-xl rounded-2xl border border-green-500/30 p-4 sm:p-6 mb-6">
              <h3 className="text-white font-bold mb-4 sm:mb-6 flex items-center gap-2 text-base sm:text-lg">
                <TrendingUp className="w-5 h-5 text-green-400" />
                Árhatás elemzés
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <p className="text-xs sm:text-sm text-gray-400 mb-1">Jelenlegi becsült ár</p>
                  <p className="text-xl sm:text-2xl font-bold text-white">{formatPrice(priceData.current)}</p>
                </div>
                
                <div>
                  <p className="text-xs sm:text-sm text-gray-400 mb-1">Optimalizált ár</p>
                  <p className="text-xl sm:text-2xl font-bold text-green-400">{formatPrice(priceData.optimized)}</p>
                </div>
                
                <div>
                  <p className="text-xs sm:text-sm text-gray-400 mb-1">Gyors eladás</p>
                  <p className="text-xl sm:text-2xl font-bold text-yellow-400">{formatPrice(priceData.quickSale)}</p>
                </div>
              </div>

              <div className="bg-secondary/20 border border-secondary/40 rounded-xl p-4 text-center">
                <p className="text-3xl sm:text-4xl md:text-5xl font-black text-secondary mb-2">
                  +{formatPrice(priceData.impact)}
                </p>
                <p className="text-xs sm:text-sm text-white">Potenciális értéknövekedés</p>
              </div>
            </div>

            {/* Suggestions */}
            <div className="bg-[#161616] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-4 sm:p-6 mb-6">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2 text-base sm:text-lg">
                <CheckCircle className="w-5 h-5 text-green-400" />
                AI Javaslatok
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 sm:p-4 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center text-xl">🎨</div>
                    <div>
                      <p className="text-white font-medium text-sm sm:text-base">Festés</p>
                      <p className="text-xs sm:text-sm text-green-400">+1.5M érték</p>
                    </div>
                  </div>
                  <input type="checkbox" className="w-5 h-5" />
                </div>

                <div className="flex items-center justify-between p-3 sm:p-4 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center text-xl">🛋️</div>
                    <div>
                      <p className="text-white font-medium text-sm sm:text-base">Bútorozás</p>
                      <p className="text-xs sm:text-sm text-blue-400">Gyorsabb eladás</p>
                    </div>
                  </div>
                  <input type="checkbox" className="w-5 h-5" />
                </div>

                <div className="flex items-center justify-between p-3 sm:p-4 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center text-xl">📸</div>
                    <div>
                      <p className="text-white font-medium text-sm sm:text-base">Profi fotó</p>
                      <p className="text-xs sm:text-sm text-purple-400">Több érdeklődő</p>
                    </div>
                  </div>
                  <input type="checkbox" className="w-5 h-5" />
                </div>
              </div>
            </div>

            {/* Estimated Time */}
            <div className="bg-[#161616] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-4 sm:p-6 mb-6">
              <div className="flex items-center gap-3">
                <Clock className="w-8 h-8 sm:w-10 sm:h-10 text-yellow-400" />
                <div>
                  <p className="text-white font-bold text-base sm:text-lg">Várható eladási idő</p>
                  <p className="text-2xl sm:text-3xl font-black text-yellow-400">14-30 nap</p>
                </div>
              </div>
            </div>

            {/* WOW Message */}
            <div className="bg-gradient-to-r from-secondary/20 to-purple-600/20 backdrop-blur-xl rounded-2xl border border-secondary/40 p-4 sm:p-6 mb-6 text-center">
              <p className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2">
                💥 Ezzel a verzióval gyorsabban eladhatod!
              </p>
              <p className="text-sm sm:text-base text-gray-300">
                Az AI-val optimalizált megjelenés átlagosan <span className="font-bold text-yellow-400">40%-kal több érdeklődőt</span> hoz
              </p>
            </div>

            {/* Main CTA */}
            <button
              onClick={() => {
                trackPublishClicked();
                navigate('/ingatlan-piac');
              }}
              className="w-full bg-secondary hover:bg-hover text-white font-bold px-6 py-5 sm:py-6 rounded-xl text-lg sm:text-xl shadow-lg transition-all active:scale-95 touch-manipulation mb-3"
            >
              🔴 Hirdetem így
            </button>

            {/* Secondary CTA */}
            <button
              onClick={() => {
                trackBoostClicked();
              }}
              className="w-full bg-white/10 hover:bg-white/20 text-white font-medium px-6 py-4 rounded-xl text-sm sm:text-base border border-white/30 transition-all active:scale-95 touch-manipulation"
            >
              💎 Kérek prémium segítséget
            </button>
          </div>
        )}
      </div>
    </>
  );
}
